/* iter55f — Tier capabilities hook.
 *
 * Reads the per-tier feature flags surfaced by `/api/auth/me` and exposes
 * a small helper `useCapabilities()` that returns:
 *
 *   {
 *     tier: 'author' | 'publisher' | 'studio' | null,
 *     cadence: 'monthly' | 'yearly' | null,
 *     can(capName): boolean,
 *     upgradeCta(capName): { title, body, target_tier }
 *   }
 *
 * Consumers use `can()` to gate features and `upgradeCta()` to render
 * the "…upgrade to Author" tooltip/bubble consistently everywhere.
 *
 * Feb-2026 tier consolidation:
 *   basic + pro + pro_plus → Author   ($24/mo)
 *   premier               → Publisher ($119/mo)
 *   studio                  unchanged  ($449/mo)
 */
import { useAuth } from './auth_context';

const TIER_LABEL = {
  // Current tiers
  author:      'Author',
  publisher:   'Publisher',
  studio:      'Studio Suite',
  studio_byok: 'Studio+ BYOK',
  // Legacy tier labels (for backwards-compat display)
  basic:       'Author',
  pro:         'Author',
  pro_plus:    'Author',
  premier:     'Publisher',
};

// Which tier a given capability first unlocks at. Used by upgrade CTAs
// so we can say "upgrade to Author" or "upgrade to Publisher" dynamically
// based on the feature the user tapped.
// Feb-2026: updated to reflect Author/Publisher/Studio tier structure.
const UNLOCK_TIER = {
  ai_write_chapters:                  'author',
  ai_rewrite_chapter:                 'author',
  bulk_draft_novel:                   'publisher',
  convert_to_script:                  'publisher',
  kdp_metadata:                       'publisher',  // KDP metadata extraction — Publisher+
  publish_bookstore:                  'author',
  isbn_manager:                       'author',
  royalties_dashboard:                'author',
  new_from_template:                  'author',
  import_synopsis:                    'author',
  create_story_bible_from_synopsis:   'author',
  publish_to_bookstore:               'author',
  claude_chat:                        'author',
  heat_map:                           'author',
  marketing_panel:                    'author',
  writer_coaching:                    'studio',    // exclusive to Studio
  monthly_editor_review:              'studio',    // exclusive to Studio
  audiobook_generation:               'author',
  audiobook_voice_cloning:            'publisher',
  full_novel_review:                  'publisher', // Full continuity walk (Publisher+)
};

const CAP_BLURB = {
  ai_write_chapters:  'Let Claude write full chapters from your outline.',
  bulk_draft_novel:   'Have Claude draft every remaining chapter back-to-back to your target word count.',
  convert_to_script:  'Adapt your novel into a Stage Play, Screenplay, or Radio Play — Fountain, .docx, .fdx, and screenwriter PDF exports.',
  kdp_metadata:       'Generate and validate your full KDP metadata set (title, description, 7 keywords, BISAC, contributors) with one click — requires Publisher plan or above.',
  publish_bookstore:  'List your finished novel on the Crown & Rose store.',
  isbn_manager:       'Register and track ISBNs for your published books.',
  royalties_dashboard:'See real-time revenue splits from bookstore sales.',
  new_from_template:  'Start new projects from genre-specific templates.',
  import_synopsis:    'Turn a paragraph pitch into a full Story Bible.',
  claude_chat:        'Open the Claude chat panel for freeform brainstorming.',
  heat_map:           'See where readers linger and drop off in each chapter.',
  marketing_panel:    'Track ads, clicks, and get campaign recommendations.',
  writer_coaching:    'Monthly 1:1 coaching from a professional novelist.',
  monthly_editor_review: 'A human proofreader + editor reads your work once a month.',
  audiobook_generation: 'Generate a full narrated audiobook of your finished novel.',
  audiobook_voice_cloning: 'Use a premium cloned voice (Fish Audio) for your audiobook narration.',
  full_novel_review:    'Run a full sequentially-structured continuity, character-drift, and pacing review across every chapter of your novel.',
};

export function useCapabilities() {
  const { user } = useAuth();
  const tier    = user?.subscription_tier || null;
  const cadence = user?.subscription_cadence || null;
  const caps    = user?.capabilities || {};

  // God Mode (admin / God Mode email) — bypass every capability gate.
  // The backend already sets `is_god_mode: true` in /api/auth/me for admins.
  const isGodMode = Boolean(user?.is_god_mode);
  const can = (name) => isGodMode || Boolean(caps[name]);

  const upgradeCta = (name) => {
    const target = UNLOCK_TIER[name] || 'pro';
    return {
      capability: name,
      target_tier: target,
      target_tier_label: TIER_LABEL[target] || 'Pro Author',
      title: `Available on ${TIER_LABEL[target] || 'Pro Author'}`,
      body: CAP_BLURB[name] || 'Upgrade your plan to unlock this feature.',
    };
  };

  return { tier, cadence, capabilities: caps, can, upgradeCta };
}
